import type { MedplumClient } from '@medplum/core'
import { config } from '@/config'

/**
 * Broker-session plumbing for the /v2/auth gateway (PD-1512, docs/AUTH_MIGRATION.md
 * §1a.3). A "broker" session holds only a Medplum ACCESS token in memory; the refresh
 * credential is an opaque rotating handle in an httpOnly cookie the middleware owns —
 * this module never sees it (fetches just say credentials:'include').
 *
 * The session-kind marker is the load-bearing rule of the parallel-operation window:
 * runtime behavior (refresh path, logout path, reload restore) keys off the MARKER set
 * at login, never off the live LaunchDarkly flag — so a flag flip or rollback changes
 * only what the next login does and can never strand a live session.
 */

export const SESSION_KIND_KEY = 'authSessionKind'

export function isBrokerSession(): boolean {
  try {
    return localStorage.getItem(SESSION_KIND_KEY) === 'broker'
  } catch {
    return false
  }
}

export function markBrokerSession(): void {
  localStorage.setItem(SESSION_KIND_KEY, 'broker')
}

export function clearBrokerSessionMarker(): void {
  localStorage.removeItem(SESSION_KIND_KEY)
  accessTokenExpiresAtMs = null
  lastBrokerRefreshMs = 0
}

// ---------------------------------------------------------------------------
// Refresh cadence

/**
 * The broker rotates the refresh handle on EVERY /v2/auth/refresh call (per-use rotation
 * is the server's reuse-detection design), so the client must not call it more often than
 * needed: refresh only when the access token is close to expiry, with a floor that keeps
 * `lastRefreshAt` comfortably inside the server's 1h idle window. Everything else
 * (reload restore, 401 recovery) still refreshes unconditionally.
 */
export const REFRESH_WHEN_EXPIRING_WITHIN_MS = 5 * 60 * 1000
export const MAX_BROKER_REFRESH_AGE_MS = 30 * 60 * 1000

let accessTokenExpiresAtMs: number | null = null
let lastBrokerRefreshMs = 0

function recordGrant(expiresAt: string | undefined): void {
  const parsed = expiresAt ? Date.parse(expiresAt) : NaN
  accessTokenExpiresAtMs = Number.isNaN(parsed) ? null : parsed
  lastBrokerRefreshMs = Date.now()
}

/** Whether the periodic tick should actually call the gateway this round. */
export function shouldRefreshBrokerSession(now: number = Date.now()): boolean {
  // No known expiry (e.g. page just loaded and restore hasn't run) — refresh to find out.
  if (accessTokenExpiresAtMs === null) return true
  if (accessTokenExpiresAtMs - now <= REFRESH_WHEN_EXPIRING_WITHIN_MS) return true
  return now - lastBrokerRefreshMs >= MAX_BROKER_REFRESH_AGE_MS
}

export interface BrokerGrant {
  accessToken: string
  accessTokenExpiresAt: string
  profileRef: string
  sessionKind: 'provider' | 'patient'
}

/**
 * Hydrate the (SDK-refresh-free) MedplumClient from a gateway grant: access token only —
 * the SDK never learns a refresh token, so it can never self-refresh against Medplum.
 * getProfileAsync() pulls /auth/me and fires the 'change' event the app's auth state
 * hangs off.
 */
export async function hydrateBrokerGrant(
  medplum: MedplumClient,
  grant: Pick<BrokerGrant, 'accessToken'> & Partial<Pick<BrokerGrant, 'accessTokenExpiresAt'>>,
): Promise<void> {
  medplum.setAccessToken(grant.accessToken)
  markBrokerSession()
  recordGrant(grant.accessTokenExpiresAt)
  await medplum.getProfileAsync()
}

export type BrokerRefreshResult =
  /** New access token hydrated into the client. */
  | 'refreshed'
  /** The gateway said 401 (expired/revoked/reused) and cleared the cookie — session over. */
  | 'unauthorized'
  /** Transport/5xx — session state unknown; keep what we have and retry later. */
  | 'unavailable'

/**
 * Refresh the broker session from the httpOnly cookie. Plain fetch (not the oRPC client)
 * so the session layer has no hook/provider dependencies and can run before React state
 * exists (reload restore).
 */
export async function refreshBrokerSession(medplum: MedplumClient): Promise<BrokerRefreshResult> {
  try {
    const response = await fetch(new URL('/v2/auth/refresh', config.middlewareUrl), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: '{}',
    })
    if (response.status === 401) {
      return 'unauthorized'
    }
    if (!response.ok) {
      return 'unavailable'
    }
    const grant = (await response.json()) as BrokerGrant
    medplum.setAccessToken(grant.accessToken)
    recordGrant(grant.accessTokenExpiresAt)
    await medplum.getProfileAsync()
    return 'refreshed'
  } catch {
    return 'unavailable'
  }
}

/** Server-side logout: deletes the broker session and clears the cookie. Best-effort. */
export async function logoutBrokerSession(): Promise<void> {
  try {
    await fetch(new URL('/v2/auth/logout', config.middlewareUrl), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: '{}',
    })
  } catch {
    // The purge task and idle timeout reap what a failed logout leaves behind.
  }
  clearBrokerSessionMarker()
}

// ---------------------------------------------------------------------------
// 401 recovery for the middleware client stacks (oRPC + legacy contracts client)

let recoverInFlight: Promise<string | undefined> | undefined

/**
 * One-shot access-token recovery after a middleware 401 (e.g. the laptop slept past the
 * refresh tick). Marker-driven like everything else: broker sessions refresh via the
 * gateway cookie; legacy sessions via the SDK. Single-flight — a burst of 401s from
 * parallel requests triggers ONE refresh, and all callers share its outcome.
 * Returns the new token, or undefined when the session is genuinely over.
 */
export function recoverAccessToken(medplum: MedplumClient): Promise<string | undefined> {
  if (!recoverInFlight) {
    recoverInFlight = doRecover(medplum).finally(() => {
      recoverInFlight = undefined
    })
  }
  return recoverInFlight
}

async function doRecover(medplum: MedplumClient): Promise<string | undefined> {
  if (isBrokerSession()) {
    const result = await refreshBrokerSession(medplum)
    if (result === 'unauthorized') {
      // The gateway says the session is over (revoked/expired/reused) — kick now instead
      // of letting callers surface raw 401s until the next SessionProvider tick.
      // clear() fires the client's 'change' event, which SessionProvider answers with
      // the full logout + redirect to /login. 'unavailable' (transport) keeps state.
      clearBrokerSessionMarker()
      medplum.clear()
    }
    return result === 'refreshed' ? medplum.getAccessToken() : undefined
  }
  try {
    await medplum.refreshIfExpired(5 * 60 * 1000)
    return medplum.getAccessToken()
  } catch {
    return undefined
  }
}
